import { useId, useRef } from 'react'
import type { AnalyticsTrendPoint } from '../analytics-types'
import { Card, Empty, formatNumber } from '../ui'

export function AnalyticsKpiCard({ icon, label, value, detail, tone = 'default' }: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  detail: React.ReactNode
  tone?: 'default' | 'attention'
}) {
  return <Card className={`analytics-kpi ${tone}`}>
    <div className="kpi-icon" aria-hidden="true">{icon}</div>
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{detail}</small>
  </Card>
}

export function SegmentTabs<T extends string>({ items, value, onChange, label, panelId }: {
  items: Array<{ key: T; label: string }>
  value: T
  onChange: (value: T) => void
  label: string
  panelId: string
}) {
  const root = useRef<HTMLDivElement>(null)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % items.length
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else return
    event.preventDefault()
    onChange(items[next].key)
    root.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
  }
  return <div ref={root} className="dashboard-tabs" role="tablist" aria-label={label}>
    {items.map((item, index) => <button
      key={item.key}
      id={`${panelId}-${item.key}-tab`}
      type="button"
      role="tab"
      aria-selected={value === item.key}
      aria-controls={panelId}
      tabIndex={value === item.key ? 0 : -1}
      className={value === item.key ? 'active' : ''}
      onClick={() => onChange(item.key)}
      onKeyDown={(event) => handleKeyDown(event, index)}
    >{item.label}</button>)}
  </div>
}

export function TrendChart({ data, valueKey, label, color = '#c8f751', formatValue = formatNumber }: {
  data: AnalyticsTrendPoint[]
  valueKey: keyof AnalyticsTrendPoint
  label: string
  color?: string
  formatValue?: (value: unknown) => string
}) {
  const gradientId = `trend-${useId().replaceAll(':', '')}`
  const summaryId = `trend-summary-${useId().replaceAll(':', '')}`
  const values = data.map((point) => Number(point[valueKey] ?? 0))
  const max = Math.max(...values, 1)
  const width = 680; const height = 230; const left = 50; const right = 18; const top = 22; const bottom = 30
  const points = values.map((value, index) => {
    const x = left + (index / Math.max(1, values.length - 1)) * (width - left - right)
    const y = top + (1 - value / max) * (height - top - bottom)
    return { x, y, value, day: String(data[index]?.day ?? '') }
  })
  const path = points.map((point, index) => `${index ? 'L' : 'M'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ')
  if (!data.length) return <Empty>Nema istorijskih podataka za izabrani period.</Empty>
  return <div className="trend-chart">
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} aria-describedby={summaryId} preserveAspectRatio="xMidYMid meet">
      <title>{label}</title>
      <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".24" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <g className="chart-grid-lines" aria-hidden="true">
        {[0, .5, 1].map((ratio) => {
          const y = top + ratio * (height - top - bottom)
          return <g key={ratio}><line x1={left} x2={width - right} y1={y} y2={y} /><text x={left - 8} y={y + 4}>{formatValue(max * (1 - ratio))}</text></g>
        })}
      </g>
      <path d={`${path} L ${points.at(-1)?.x ?? right} ${height - bottom} L ${points[0]?.x ?? left} ${height - bottom} Z`} fill={`url(#${gradientId})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="3" vectorEffect="non-scaling-stroke" />
      {points.length <= 31 && points.map((point) => <circle key={point.day} className="chart-point" cx={point.x} cy={point.y} r="4" fill={color} tabIndex={0} aria-label={`${point.day}: ${formatValue(point.value)}`}><title>{point.day}: {formatValue(point.value)}</title></circle>)}
    </svg>
    <p id={summaryId} className="sr-only">Period od {String(data[0]?.day ?? '')} do {String(data.at(-1)?.day ?? '')}. Posljednja vrijednost je {formatValue(values.at(-1) ?? 0)}.</p>
    <div className="chart-axis"><span>{String(data[0]?.day ?? '')}</span><b>{formatValue(values.at(-1) ?? 0)} trenutno</b><span>{String(data.at(-1)?.day ?? '')}</span></div>
  </div>
}

export function HealthMetric({ icon, label, value, detail, rate, tone = 'lime' }: {
  icon: React.ReactNode
  label: string
  value: string
  detail: string
  rate: number | null
  tone?: 'lime' | 'blue' | 'amber'
}) {
  const percentage = rate === null ? 0 : Math.round(Math.max(0, Math.min(1, rate)) * 100)
  return <div className={`health-metric ${tone}`}>
    <div className="health-metric-head"><span className="health-icon" aria-hidden="true">{icon}</span><span><small>{label}</small><strong>{value}</strong></span></div>
    <div className="health-progress" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={rate === null ? undefined : percentage} aria-valuetext={rate === null ? 'Podaci nisu dostupni' : value}><i style={{ width: `${percentage}%` }} /></div>
    <p>{detail}</p>
  </div>
}
